import React from 'react'
import { logBreadcrumb, recordError } from '../../services/crashlytics/crashlyticsService'

// A deep tree can produce a multi-kilobyte stack; the top frames are the useful ones.
const MAX_COMPONENT_STACK_LENGTH = 2048

interface Props {
  children: React.ReactNode
}

interface State {
  hasError: boolean
}

/**
 * Catches render-time errors anywhere below it, reports them to Crashlytics and
 * shows a reload prompt instead of a blank screen.
 *
 * Mounted outside the theme/intl providers so it still works when one of those
 * is what threw — hence the inline styles and untranslated copy.
 */
class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Awaited in sequence: both calls cross the Capacitor bridge, and recordError
    // closes the report — so the component stack has to land before it, or it
    // attaches to the next report instead of this one.
    void (async () => {
      const componentStack = (errorInfo.componentStack ?? '').slice(0, MAX_COMPONENT_STACK_LENGTH)

      await logBreadcrumb(`React component stack:${componentStack}`)
      await recordError(error, { source: 'react-error-boundary' })
    })()
  }

  handleReload = (): void => {
    window.location.reload()
  }

  render(): React.ReactNode {
    if (!this.state.hasError) {
      return this.props.children
    }

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: 24,
          gap: 16,
          textAlign: 'center',
          fontFamily: 'sans-serif',
        }}
      >
        <p style={{ margin: 0, fontSize: 16 }}>Something went wrong.</p>
        <button
          type='button'
          onClick={this.handleReload}
          style={{
            padding: '10px 24px',
            fontSize: 16,
            borderRadius: 4,
            border: 0,
            color: '#fff',
            backgroundColor: '#1976d2',
          }}
        >
          Reload
        </button>
      </div>
    )
  }
}

export default ErrorBoundary
